import { customInstance } from '@/features/auth/axios-config'
import {
  RequestListNotifications,
  SuccessResponseListNotifications,
} from '@/schema'

import {
  JoinGroupsRequest,
  LeaveGroupsRequest,
  NegotiateResponse,
} from './types'

const API_ENDPOINT_NOTIFICATIONS = '/admin/v1/notifications'
const NOTIFICATION_BASE_PATH = '/admin/v1'

export const negotiateConnection = async (): Promise<NegotiateResponse> => {
  return customInstance<NegotiateResponse>({
    method: 'post',
    url: `${NOTIFICATION_BASE_PATH}/negotiate`,
  })
}

export const joinGroups = async (request: JoinGroupsRequest): Promise<void> => {
  return customInstance<void>({
    method: 'post',
    url: `${NOTIFICATION_BASE_PATH}/join`,
    data: request,
  })
}

export const leaveGroups = async (
  request: LeaveGroupsRequest
): Promise<void> => {
  return customInstance<void>({
    method: 'post',
    url: `${NOTIFICATION_BASE_PATH}/leave`,
    data: request,
  })
}

/**
 * Notification API service
 *
 * Provides methods for interacting with notification endpoints
 */
export const notificationApi = {
  /**
   * List notifications for the authenticated user with pagination and filtering
   *
   * @param data - Request body containing filters, pagination, and search parameters
   * @returns Promise resolving to paginated list of notifications with metadata
   *
   * @example
   * ```typescript
   * const response = await notificationApi.listNotifications({
   *   filter_by: {
   *     notification_type: [NotificationType.Mention]
   *   },
   *   limit: 50,
   *   offset: 0,
   *   q: "mention",
   *   sort_by: "+created_at",
   *   variant: "list"
   * })
   * ```
   */
  listNotifications: async (
    data: RequestListNotifications
  ): Promise<SuccessResponseListNotifications> => {
    return customInstance<SuccessResponseListNotifications>({
      method: 'POST',
      url: `${API_ENDPOINT_NOTIFICATIONS}/list`,
      data,
    })
  },

  /**
   * Mark a notification as read
   *
   * @param id - The notification ID to mark as read
   * @returns Promise resolving when the notification is marked as read
   *
   * @example
   * ```typescript
   * await notificationApi.markAsRead('notification-123')
   * ```
   */
  markAsRead: async (id: string): Promise<void> => {
    return customInstance<void>({
      method: 'PATCH',
      url: `${API_ENDPOINT_NOTIFICATIONS}/${id}/read`,
    })
  },

  /**
   * Mark a notification as unread
   *
   * @param id - The notification ID to mark as unread
   * @returns Promise resolving when the notification is marked as unread
   *
   * @example
   * ```typescript
   * await notificationApi.markAsUnread('notification-123')
   * ```
   */
  markAsUnread: async (id: string): Promise<void> => {
    return customInstance<void>({
      method: 'PATCH',
      url: `${API_ENDPOINT_NOTIFICATIONS}/${id}/unread`,
    })
  },

  markAllAsRead: async (): Promise<void> => {
    return customInstance<void>({
      method: 'PUT',
      url: `${API_ENDPOINT_NOTIFICATIONS}/mark-all-read`,
    })
  },
}
